import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
    Grid, Button, CircularProgress
} from '@mui/material';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { variables } from '../Variables';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/en'; // or your locale

dayjs.extend(weekday);
dayjs.extend(localizedFormat);
dayjs.locale('en');

const defaultFilter = {
    PageNo: 1,
    PageSize: 10,
    OrderBy: '',
    OrderType: '',
    SubCategoryName: '',
    SubCategoryId: null,
    CategoryId: null,
    CreatedOn: null,
    UpdatedOn: null,
};

export const Subcategories = ({ showMessage }) => {
    const user = useSelector((state) => state.user.user);
    const jsonconfig = {
        headers: {
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'application/json'
        }
    };

    const [subCategory, setSubCategory] = useState(null);
    const [categoriesList, setCategoriesList] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [editSubCategoryId, setEditSubCategoryId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState(defaultFilter);
    const [subCategoriesList, setSubCategoriesList] = useState([]);
    const [totalCategories, setTotalCategories] = useState(0);
    const [categoryId, setCategoryId] = useState("");
    const [errors, setErrors] = useState({});
    const [subCategoryStatus, setSubCategoryStatus] = useState(null);

    const validate = () => {
        const validationErrors = {};

        if (!subCategory.subCategoryName || subCategory.subCategoryName.trim() === '') {
            validationErrors.subCategoryName = 'SubCategory Name is required.';
        }

        if (!categoryId || categoryId === '') {
            validationErrors.categoryId = 'Category is required.';
        }

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const resetForm = () => {
        setSubCategory(null);
        setEditMode(false);
        setEditSubCategoryId(null);
        setCategoryId("");
        setErrors({});
    };

    const handleFieldChange = (field, value) => {
        if (field == "categoryId") {
            setCategoryId(value);
        }
        setSubCategory(prev => ({ ...prev, [field]: value }));

        // Reset field error
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const url = variables.API_URL + `ClothingSubcategory/${editMode ? 'UpdateSubCategory' : 'CreateSubCategory'}`;
            let payload = {
                ...subCategory,
                createdBy: user.user.userId,
                categoryId: categoryId
            };

            const isActive = subCategoryStatus === "true" || subCategoryStatus === true;
            if (editMode && payload.isActive != isActive) {
                payload.isActive = isActive
            }
            await axios.post(url, payload, jsonconfig);
            showMessage('success', `SubCategory ${editMode ? 'updated' : 'created'} successfully.`);
            resetForm();
            loadSubCategories();
        } catch (error) {
            showMessage('error', error.response?.data || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this subCategory?")) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${variables.API_URL}ClothingSubcategory/InActiveSubCategoryById?id=${id}`,
                {},
                jsonconfig
            );

            showMessage('success', response?.data);
            loadSubCategories();
        } catch (error) {
            const errorMsg = error.response?.data || "Something went wrong while deleting.";
            showMessage("error", errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        { field: 'subCategoryId', headerName: 'ID', width: 50 },
        { field: 'categoryId', headerName: 'CategoryID', width: 100 },
        { field: 'subCategoryName', headerName: 'SubCategory Name', width: 150 },
        {
            field: 'createdOn', headerName: 'Created On',
            valueFormatter: (params) =>
                dayjs(params.value).format('ddd-MMM-YYYY HH:mm'),
            width: 200
        },
        {
            field: 'updatedOn', headerName: 'Updated On',
            valueFormatter: (params) =>
                dayjs(params.value).format('ddd-MMM-YYYY HH:mm'),
            width: 200
        },
        { field: 'isActive', headerName: 'Visibility', width: 90, disableColumnMenu: true },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 180,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <>
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(params.row.subCategoryId)}
                        style={{ marginRight: 8 }}
                    >
                        Delete
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                            setEditMode(true);
                            setEditSubCategoryId(params.row.subCategoryId);
                            setCategoryId(params.row.categoryId);
                            setSubCategoryStatus(params.row.isActive);
                            setSubCategory({
                                ...params.row
                            });
                        }}
                    >
                        Edit
                    </Button>
                </>
            )
        }
    ];

    const loadSubCategories = async () => {
        setLoading(true);
        try {
            const res = await axios.post(
                variables.API_URL + 'ClothingSubcategory/GetAllSubCategoriesForAdmin',
                filter,
                jsonconfig
            );
            setSubCategoriesList(res.data.subCategories || []);
            setTotalCategories(res.data.totalCount || 0);
        } catch (error) {
            showMessage('error', 'Failed to load subCategories.');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setFilter(prev => ({ ...prev, pageNo: newPage + 1 }));
    };

    const handlePageSizeChange = (newSize) => {
        setFilter(prev => ({ ...prev, pageSize: newSize, pageNo: 1 }));
    };

    const loadCategories = async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                variables.API_URL + 'ClothingCategory/GetAllCategories'
            );
            setCategoriesList(res.data || []);
        } catch (error) {
            showMessage('error', 'Failed to load categories.');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (filterModel) => {
        // Build fresh filter based on default + active filters
        const newFilter = {
            ...filter,
            SubCategoryName: '',
            SubCategoryId: null,
            CategoryId: null,
            CreatedOn: null,
            UpdatedOn: null,
            PageNo: 1 // always reset to first page on filter
        };

        const activeFilters = filterModel.items.filter(
            (item) => item.value !== undefined && item.value !== null && item.value !== ''
        );

        activeFilters.forEach(({ field, value }) => {
            switch (field) {
                case 'SubCategoryName':
                    newFilter.SubCategoryName = value.trim?.() || value;
                    break;
                case 'subCategoryId':
                    newFilter.SubCategoryId = parseInt(value);
                    break;
                case 'categoryId':
                    newFilter.CategoryId = parseInt(value);
                    break;
                case 'createdOn':
                    newFilter.CreatedOn = value;
                    break;
                case 'updatedOn':
                    newFilter.UpdatedOn = value;
                    break;
                // Add more cases as needed
            }
        });

        if (JSON.stringify(filter) !== JSON.stringify(newFilter)) {
            setFilter(newFilter);
        }
        //setFilter(newFilter);
    };


    useEffect(() => {
        loadSubCategories();
    }, [filter.PageNo, filter.PageSize, filter.SubCategoryId, filter.CategoryId, filter.OrderBy, filter.OrderType, filter.CategoryName, filter.CreatedOn, filter.UpdatedOn]);

    useEffect(() => {
        if (categoriesList.length == 0) {
            loadCategories();
        }
    });

    return (
        <>
            <div className="row">
                <div className="col-sm-12 px-4">
                    <div className="form-container mb-3">
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                <ul className="text-start list-style-none mb-0">
                                    {Object.values(errors).map((err, idx) => (
                                        <li key={idx}>{idx + 1}. {err}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <label className="col-sm-4 col-lg-2 form-group-label mb-3">Categories</label>
                                <div className="col-sm-8 col-lg-4 mb-3">
                                    <select className={`form-select ${errors.categoryId ? 'border-red' : ''}`} value={categoryId}
                                        onChange={(e) => handleFieldChange('categoryId', e.target.value)}>
                                        <option value="">Select Category</option>
                                        {
                                            categoriesList.length > 0 ? categoriesList.map(item => {
                                                return (
                                                    <option key={item.categoryId} value={item.categoryId}>{item.categoryName}</option>
                                                )
                                            })
                                                :
                                                <></>
                                        }
                                    </select>
                                </div>

                                <label className="col-sm-4 col-lg-2 form-group-label mb-3">SubCategory Name</label>
                                <div className="col-sm-8 col-lg-4 mb-3">
                                    <input
                                        type="text"
                                        id="name"
                                        className={`form-control ${errors.subCategoryName ? 'border-red' : ''}`}
                                        value={subCategory ? subCategory.subCategoryName : ""}
                                        onChange={(e) => handleFieldChange('subCategoryName', e.target.value)}
                                    />
                                </div>
                                {
                                    editMode ?
                                        <>
                                            <label className="col-sm-4 col-lg-2 form-group-label mb-3">Status</label>
                                            <div className="col-sm-8 col-lg-4 mb-3">
                                                <select className="form-select" value={subCategoryStatus} onChange={(e) => setSubCategoryStatus(e.target.value)}>
                                                    <option value="">Select Status</option>
                                                    <option value="true">Active</option>
                                                    <option value="false">InActive</option>
                                                </select>
                                            </div>
                                        </>
                                        :
                                        <></>
                                }
                                <div className="col-12 text-end">
                                    <button type="submit" className="site-btn" disabled={loading}>
                                        {loading ? 'Saving...' : editMode ? 'Update SubCategory' : 'Upload SubCategory'}
                                    </button>
                                    {editMode && (
                                        <button type="button" className="site-btn ms-2" onClick={resetForm}>
                                            Cancel Edit
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12 px-4">
                    <span className="form-title">Categories List</span>
                    <div className="form-container mb-3" style={{ height: 500, width: '100%' }}>
                        {loading ? (
                            <CircularProgress />
                        ) : (
                            <DataGrid
                                rows={subCategoriesList}
                                columns={columns}
                                getRowId={(row) => row.subCategoryId}
                                rowCount={totalCategories}
                                loading={loading}
                                paginationMode="server"
                                sortingMode="server"
                                filterMode="server"
                                onFilterModelChange={handleFilterChange}
                                paginationModel={{
                                    page: filter.PageNo - 1,
                                    pageSize: filter.PageSize,
                                }}
                                onPaginationModelChange={({ page, pageSize }) => {
                                    setFilter(prev => ({
                                        ...prev,
                                        PageNo: page + 1,
                                        PageSize: pageSize,
                                    }));
                                }}
                                sortModel={
                                    filter.OrderBy && filter.OrderType
                                        ? [{ field: filter.OrderBy, sort: filter.OrderType }]
                                        : []
                                }
                                onSortModelChange={(sortModel) => {
                                    if (sortModel.length > 0 && (sortModel[0].sort === 'asc' || sortModel[0].sort === 'desc')) {
                                        const { field, sort } = sortModel[0];
                                        setFilter(prev => ({
                                            ...prev,
                                            OrderBy: field,
                                            OrderType: sort
                                        }));
                                    } else {
                                        // Clear sort only if model is empty (not invalid)
                                        setFilter(prev => ({
                                            ...prev,
                                            OrderBy: '',
                                            OrderType: ''
                                        }));
                                    }
                                }}
                                pageSizeOptions={[10, 20, 50]}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
